import type { ThemeColors } from '@components/Theme'
import { deepPurple, lime, orange, pink, teal } from '@mui/material/colors'

export const dark: ThemeColors = {
  active: {
    1: '#22D3EE', // teal-400 - primary
    2: '#06B6D4', // teal-500
    3: '#0891B2', // teal-600
    4: '#155E75', // teal-800
    5: '#164E63', // teal-900
  },
  // One step further from the canvas than active[1] — on navy that means
  // *brighter* (teal-300), so `primary.dark` hovers still take the near-black
  // `primary.contrastText` label. Backs `palette.primary.dark`.
  activeEmphasis: '#67E8F9', // teal-300
  alert: {
    icon: {
      error: '#F87171', // red-400
      info: '#F8FAFC', // slate-50
      success: '#4ADE80', // green-400 - brighter on navy
      warning: '#FBBF24', // amber-400
    },
    paper: {
      error: '#991B1B', // red-800
      info: '#1B3349', // raised navy
      success: '#166534', // green-800
      warning: '#92400E', // amber-800
    },
  },
  autofill: '#164E63',
  background: {
    default: '#0B1B2B', // deep navy canvas
    paper: '#13283D', // navy paper
    raised: '#1B3349', // raised navy - icon chips, nested headers
  },
  checkbox: {
    default: '#F8FAFC',
  },
  disabled: '#7E92AB', // legible disabled on navy
  divider: 'rgba(148, 163, 184, 0.18)',
  entity: {
    Case: {
      dark: teal[400],
      light: teal[900],
      main: teal[500],
    },
    Customer: {
      dark: orange[400],
      light: orange[900],
      main: orange[500],
    },
    MediationTicket: {
      dark: lime[400],
      light: lime[900],
      main: lime[500],
    },
    NegotiationTicket: {
      dark: deepPurple[300],
      light: deepPurple[900],
      main: deepPurple[500],
    },
    Order: {
      dark: pink[400],
      light: pink[900],
      main: pink[500],
    },
  },
  error: {
    dark: '#F87171', // red-400 - brighter on navy
    light: '#FEE2E2', // red-100
    mid: '#FCA5A5', // red-300
    verydark: '#DC2626', // red-600
  },
  errorAction: {
    1: '#F87171', // red-400 - brighter on navy
    2: 'rgba(248, 113, 113, 0.8)',
    3: 'rgba(248, 113, 113, 0.6)',
    4: 'rgba(248, 113, 113, 0.4)',
    5: 'rgba(248, 113, 113, 0.2)',
  },
  gray: {
    default: '#1B3349', // raised navy
    disabled: '#475569', // slate-600
  },
  highlightInactive: {
    default: '#8195AD', // legible hint on navy
  },
  info: {
    dark: '#38BDF8', // sky-400 - brighter on navy
    light: '#E0F2FE', // sky-100
    mid: '#7DD3FC', // sky-300
    verydark: '#0369A1', // sky-700
  },
  main: {
    1: '#F1F5F9', // slate-100
    2: '#CBD5E1', // slate-300
    3: '#AFBDD0', // brighter secondary for legibility on navy
    4: '#7E92AB', // legible disabled on navy
    5: 'rgba(148, 163, 184, 0.08)',
  },
  paperBorder: 'rgba(148, 163, 184, 0.22)',
  success: {
    dark: '#4ADE80', // green-400 - brighter on navy
    light: '#DCFCE7', // green-100
    mid: '#86EFAC', // green-300
    verydark: '#4CA154', // progress green
  },
  theme: 'dark',
  warning: {
    dark: '#FBBF24', // amber-400 - brighter on navy
    light: '#FEF3C7', // amber-100
    mid: '#FCD34D', // amber-300
    verydark: '#B45309', // amber-700
  },
}
export const light: ThemeColors = {
  active: {
    1: '#0891B2', // teal-600 - primary
    2: '#06B6D4', // teal-500
    3: '#22D3EE', // teal-400
    4: '#67E8F9', // teal-300
    5: '#CFFAFE', // teal-100
  },
  // One step further from the canvas than active[1] — on the light canvas that
  // means *darker* (teal-700), so `primary.dark` hovers still take the
  // near-white `primary.contrastText` label. Backs `palette.primary.dark`.
  activeEmphasis: '#0E7490', // teal-700
  alert: {
    icon: {
      error: '#DC2626', // red-600
      info: '#0F172A', // slate-900
      success: '#4CA154', // progress green
      warning: '#D97706', // amber-600
    },
    paper: {
      error: '#FEE2E2', // red-100
      info: '#FFFFFF',
      success: '#DCFCE7', // green-100
      warning: '#FEF3C7', // amber-100
    },
  },
  autofill: '#CFFAFE', // teal-100
  background: {
    default: '#F4F7FA', // faintly cool app canvas
    paper: '#FFFFFF',
    raised: '#F1F5F9', // slate-100 - icon chips, nested headers
  },
  checkbox: {
    default: '#475569', // slate-600
  },
  disabled: '#6B7A8E', // darker for legibility
  divider: '#E2E8F0', // slate-200
  entity: {
    Case: {
      dark: teal[700],
      light: teal[100],
      main: teal[500],
    },
    Customer: {
      dark: orange[700],
      light: orange[100],
      main: orange[500],
    },
    MediationTicket: {
      dark: lime[700],
      light: lime[100],
      main: lime[500],
    },
    NegotiationTicket: {
      dark: deepPurple[700],
      light: deepPurple[100],
      main: deepPurple[500],
    },
    Order: {
      dark: pink[700],
      light: pink[100],
      main: pink[500],
    },
  },
  error: {
    dark: '#DC2626', // red-600
    light: '#FEE2E2', // red-100
    mid: '#FCA5A5', // red-300
    verydark: '#B91C1C', // red-700
  },
  errorAction: {
    1: '#DC2626', // red-600
    2: '#EF4444', // red-500
    3: '#F87171', // red-400
    4: '#FCA5A5', // red-300
    5: '#FEE2E2', // red-100
  },
  gray: {
    default: '#CBD5E1', // slate-300
    disabled: '#6B7A8E', // darker for legibility
  },
  highlightInactive: {
    default: '#5B6B7F', // darker for legibility
  },
  info: {
    dark: '#0284C7', // sky-600
    light: '#E0F2FE', // sky-100
    mid: '#7DD3FC', // sky-300
    verydark: '#0369A1', // sky-700
  },
  main: {
    1: '#0F172A', // slate-900 - darker for better contrast
    2: '#334155', // slate-700
    3: '#475569', // slate-600
    4: '#6B7A8E', // darker for legibility
    5: 'rgba(15, 23, 42, 0.06)',
  },
  paperBorder: '#E2E8F0', // slate-200
  success: {
    dark: '#4CA154', // progress green
    light: '#DCFCE7', // green-100
    mid: '#86EFAC', // green-300
    verydark: '#3D8144', // darker progress green
  },
  theme: 'light',
  warning: {
    dark: '#D97706', // amber-600
    light: '#FEF3C7', // amber-100
    mid: '#FCD34D', // amber-300
    verydark: '#B45309', // amber-700
  },
}
